import styled from '@emotion/styled';
import produce from 'immer';
import { createElement, FC, SyntheticEvent } from 'react';
import { useToggle } from 'react-use';
import { useRecoilValue } from 'recoil';

import { DynamicSchemaDefinitionFileIds, DynamicSchemaDefinitionSection } from '../../../../generated/graphql';
import { SPACING_REGULAR } from '../../../constant/spacing';
import { editingProjectAssetFilesStateAtom } from '../../../page/ProjectEditingPage/atoms/editingProjectStateAtom';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { DynamicSchemaValue, ProjectAssetEditorFieldValues } from '../../../types/DynamicSchema';
import Field from './Field';

type Props = {
  section: DynamicSchemaDefinitionSection;
  value: DynamicSchemaValue;
  bucketId: string;
  onChange: (e: SyntheticEvent<HTMLElement>, value: DynamicSchemaValue) => void;
  className?: string;
};

const ProjectAssetEditor: FC<Props> = ({ section, value, bucketId, onChange, className }) => {
  const { uploadingAssetFilesProgress } = useRecoilValue(editingProjectAssetFilesStateAtom);

  const [enableCategory, toggleEnableCategory] = useToggle(true);

  const fileIdsField = section.fields.find(
    (field): field is DynamicSchemaDefinitionFileIds => field.type === 'FILE_IDS',
  );
  if (fileIdsField == null) {
    throw new Error();
  }

  const fields = section.fields.map((field) => {
    if (section.meta == null) {
      return null;
    }

    return (
      <Field
        field={field}
        value={value.values[field.key] as ProjectAssetEditorFieldValues}
        bucketId={bucketId}
        meta={section.meta}
        onChange={(e, v) =>
          onChange(
            e,
            produce(value, (draft) => {
              draft.values[field.key] = v;
            }),
          )
        }
        enableCategory={enableCategory}
        toggleEnableCategory={toggleEnableCategory}
        filesProgress={uploadingAssetFilesProgress[fileIdsField.key] || {}}
        key={field.key}
      />
    );
  });

  return <Root className={className}>{fields}</Root>;
};

const Root = styled.div`
  ${flexibleLayoutContainer({ direction: 'column' })}
  padding-bottom: ${SPACING_REGULAR}
`;

export default ProjectAssetEditor;
